/**
 * OnboardingHints · contextual first-visit hints of the onboarding.
 * slot_onboarding_redesign OB.3 (23 MAY 2026) · extended slot_t00_ftue_rewrite
 * v2.1 (7 JUN 2026 · Genie ack): each major screen explains itself on the
 * student's first visit — board (columns + list mode), course map, progress,
 * profile; the nav hint also covers themes + the A/A+/A++ text-size control.
 *
 * NOT a sequential tour — each hint is an independent coachmark anchored to a
 * real, live element, shown at most once, dismissed individually. Only one
 * hint renders at a time (priority order); a hint shows only when its anchor
 * exists in the DOM, its screen condition holds, and it has not been seen.
 *
 * Anchors use existing production classes (`.side-rail`, `.glossary-term`,
 * `.pomo-widget-fab`, `.view-toggle`, `.sprints-hero`, `.prog-hero`,
 * `.profile-hero`) — no new `data-tour` attributes required.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import type { OnboardingHintKey } from './onboarding-state';

type Placement = 'right' | 'below' | 'above-left';

interface HintDef {
  key: OnboardingHintKey;
  /** Live production selector to anchor against. */
  selector: string;
  placement: Placement;
  tagClass: string;
  tag: string;
  title: string;
  body: string;
  /** Screens on which this hint is eligible. */
  screens: ReadonlySet<string>;
}

/** Priority order — earlier entries win when several are eligible at once. */
const HINTS: ReadonlyArray<HintDef> = [
  {
    key: 'nav',
    selector: '.side-rail',
    placement: 'right',
    tagClass: '',
    tag: 'НАВИГАЦИЯ',
    title: 'Меню студии',
    body: 'Слева — все разделы. Наведи курсор — рейка развернётся с подписями. Внизу меню — переключатель темы и размер текста A / A+ / A++.',
    screens: new Set(['dashboard']),
  },
  {
    key: 'glossaryTerm',
    selector: '.glossary-term',
    placement: 'below',
    tagClass: 'onb-tag-teal',
    tag: 'В ТИКЕТЕ',
    title: 'Слова с пунктиром',
    body: 'Подчёркнутый термин — словарный. Наведи на него — всплывёт определение.',
    screens: new Set(['ticket']),
  },
  {
    key: 'board',
    selector: '.view-toggle',
    placement: 'below',
    tagClass: 'onb-tag-teal',
    tag: 'ДОСКА',
    title: 'Три колонки и список',
    body: 'Путь тикета: «К работе» → «В работе» → «Сделано». Откроешь тикет — он сам в работе, закроешь — уедет в «Сделано». Эта кнопка переключает доску в вид списком — попробуй оба.',
    screens: new Set(['sprint']),
  },
  {
    key: 'map',
    selector: '.sprints-hero',
    placement: 'below',
    tagClass: 'onb-tag-teal',
    tag: 'КАРТА',
    title: 'Маршрут курса',
    body: 'Каждая остановка — спринт: глава на одну тему. Открываются по порядку, ты в самом начале.',
    screens: new Set(['sprints']),
  },
  {
    key: 'progress',
    selector: '.prog-hero',
    placement: 'below',
    tagClass: 'onb-tag-mustard',
    tag: 'ПРОГРЕСС',
    title: 'Твои бейджи и навыки',
    body: 'Растут от закрытых тикетов. Заглядывай, когда захочется увидеть, сколько уже пройдено.',
    screens: new Set(['progress']),
  },
  {
    key: 'profile',
    selector: '.profile-hero',
    placement: 'below',
    tagClass: '',
    tag: 'ПРОФИЛЬ',
    title: 'Имя и настройки',
    body: 'Здесь меняется имя. Отсюда же можно перепройти знакомство со студией.',
    screens: new Set(['profile']),
  },
  {
    key: 'pomodoro',
    selector: '.pomo-widget-fab',
    placement: 'above-left',
    tagClass: 'onb-tag-mustard',
    tag: 'ТАЙМЕР',
    title: 'Фокус 25 / 5',
    body: 'Нужна пауза? Pomodoro-таймер фокуса — вот эта кнопка справа внизу.',
    screens: new Set(['ticket', 'sprint']),
  },
];

const BUBBLE_W = 250;

/**
 * Mobile breakpoint (6 JUN 2026 · mobile nav batch). At/below this width the
 * anchor math is abandoned entirely — the bubble renders as a fixed bottom sheet
 * (`left:16; right:16; bottom:16` via `.onb-hint--sheet`). The previous
 * clamp-only fix (vw-24 → vw-32) still left the `above-left` Pomodoro-FAB hint
 * off the right edge on narrow phones: anchoring a 250px-intent bubble to a
 * bottom-right element on a ~360px screen is unwinnable. A bottom sheet sidesteps
 * positioning math and matches the checklist's own mobile treatment.
 */
const MOBILE_BP = 480;

/** Compute a fixed-position style for the bubble relative to its anchor rect.
 *
 * slot_responsive_overhaul RO.6 fix (2026-05-31):
 * onboarding.css line 248 caps actual bubble width to `calc(100vw - 32px)`
 * on mobile (max-width: 640px) - but position math here used BUBBLE_W=250
 * constant, so on narrow viewports (<282px effective) tooltip overflowed past
 * the right edge (clampL allowed left = vw-262, actual width = vw-32, total
 * overshoot ~30-66px depending on viewport). Use actualW = min(BUBBLE_W, vw-24)
 * to match the rendered width - clampL now keeps tooltip fully on-screen.
 */
function place(rect: DOMRect, placement: Placement): CSSProperties {
  const vw = window.innerWidth;
  // Match the rendered width cap exactly: CSS `.onb-hint` is `max-width: calc(100vw - 32px)`.
  // (Was vw - 24, 8px looser than CSS → bubble could still clip on the right.)
  const actualW = Math.min(BUBBLE_W, vw - 32);
  const clampL = (l: number) => Math.max(12, Math.min(l, vw - actualW - 12));
  if (placement === 'right') {
    return { left: clampL(rect.right + 14), top: Math.max(12, rect.top + 72) };
  }
  if (placement === 'below') {
    return { left: clampL(rect.left), top: rect.bottom + 10 };
  }
  // above-left — anchored above a bottom-right element (Pomodoro FAB)
  return { left: clampL(rect.left + rect.width - actualW), top: Math.max(12, rect.top - 132) };
}

interface OnboardingHintsProps {
  screen: string;
  seen: Record<OnboardingHintKey, boolean>;
  onSeen: (key: OnboardingHintKey) => void;
}

export function OnboardingHints({ screen, seen, onSeen }: OnboardingHintsProps) {
  // Recompute on scroll/resize so the anchored bubble tracks its element.
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, { passive: true });
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump);
    };
  }, [bump]);

  // Anchor may mount slightly after the screen does — re-check shortly after.
  useEffect(() => {
    const id = window.setTimeout(bump, 350);
    return () => window.clearTimeout(id);
  }, [screen, bump]);

  // On phones, drop anchor positioning entirely and render as a bottom sheet
  // (see MOBILE_BP). `bump` already re-renders on resize, so this re-evaluates.
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_BP;

  // Pick the first eligible hint in priority order.
  let active: { def: HintDef; style: CSSProperties } | null = null;
  for (const def of HINTS) {
    if (seen[def.key]) continue;
    if (!def.screens.has(screen)) continue;
    const el = typeof document !== 'undefined' ? document.querySelector(def.selector) : null;
    if (!el) continue;
    // Mobile sheet: position comes from `.onb-hint--sheet` CSS, not inline math.
    active = { def, style: isMobile ? {} : place(el.getBoundingClientRect(), def.placement) };
    break;
  }

  if (!active) return null;
  const { def, style } = active;

  return (
    <div
      className={`onb-card onb-hint${isMobile ? ' onb-hint--sheet' : ''}`}
      style={style}
      role="dialog"
      aria-labelledby={`onb-hint-${def.key}-title`}
    >
      <span className={`onb-hint-tag ${def.tagClass}`.trim()}>{def.tag}</span>
      <div id={`onb-hint-${def.key}-title`} className="onb-hint-title">
        {def.title}
      </div>
      <div className="onb-hint-body">{def.body}</div>
      <div className="onb-hint-foot">
        <button type="button" className="onb-hint-dismiss" onClick={() => onSeen(def.key)}>
          Понятно
        </button>
      </div>
    </div>
  );
}
